import React from 'react';
import {DataInfo, StyledGrid, TagChip} from 'presentation/components';
import {Button, Divider, Stack, Typography, useTheme} from '@mui/material';
import {StyledCard} from '../../styles';
import {FavoriteDoctorsLikeButton} from '../FavoriteDoctorsLikeButton';
import {formatText} from 'presentation/utils';
import {ProviderCardProps} from './types';
import {ChipColorEnum} from 'presentation/components/TagChip/types';
import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {FeaturesEnum} from 'presentation/constants';

const {scheduleInfoChip} = translations['pt-br'].privateFlow.guideFlow.favoriteDoctors;

const ProviderCard = ({
	professional,
	iconName,
	primaryActionLabel,
	onScheduleAppointment,
}: ProviderCardProps) => {
	const {isEnabled: isFavoriteDoctorsImprovementsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_FAVORITE_DOCTORS_IMPROVEMENTS,
	);
	const {palette} = useTheme();
	const disabledColor = palette.grey[500];
	const textPrimaryColor = palette.text.primary;
	const iconPrimaryColor = palette.primary.main;
	const concatenatedSpecialty = professional.nmEspecialidade.concat(
		professional.nmSubEspecialidade ? ` - ${professional.nmSubEspecialidade}` : '',
	);

	return (
		<StyledGrid.Item
			key={`${professional.cdPrestadorFisico}-${professional.cdEspecialidade}`}
			sm={12}
			md={6}
			lg={6}
			sx={{display: 'flex'}}>
			<StyledCard elevation={0}>
				{!professional.flDescredenciado && isFavoriteDoctorsImprovementsEnabled ? (
					<Stack pt={2} px={2} gap={1} direction="row" alignItems="center">
						<TagChip
							label={
								professional.flAgendaDisponivel
									? scheduleInfoChip.available
									: scheduleInfoChip.unavailable
							}
							color={
								professional.flAgendaDisponivel
									? ChipColorEnum.SUCCESS
									: ChipColorEnum.WARNING
							}
						/>
					</Stack>
				) : null}
				<Stack
					pt={professional.flDescredenciado ? 2 : 1}
					px={2}
					gap={1}
					direction="row"
					justifyContent="space-between"
					alignItems="center">
					<Typography
						variant="h6"
						fontSize="1.125rem"
						color={professional.flDescredenciado ? disabledColor : textPrimaryColor}>
						{formatText(professional.nmPrestadorFisico)}
					</Typography>
					{professional.flDescredenciado ? (
						<Icon name="heart-filled" color={disabledColor} />
					) : (
						<FavoriteDoctorsLikeButton
							cdPrestador={professional.cdPrestadorFisico}
							cdEspecialidade={professional.cdEspecialidade}
							cdSubEspecialidade={professional.cdSubEspecialidade}
							selected
						/>
					)}
				</Stack>
				<Stack px={2} flex={1}>
					<DataInfo
						iconName={iconName}
						primaryText={formatText(concatenatedSpecialty)}
						iconColor={professional.flDescredenciado ? disabledColor : iconPrimaryColor}
						primaryTextColor={
							professional.flDescredenciado ? disabledColor : textPrimaryColor
						}
					/>
				</Stack>
				<Divider />
				<Stack py={1} px={2} alignItems="flex-end">
					{professional.flDescredenciado && isFavoriteDoctorsImprovementsEnabled ? (
						<FavoriteDoctorsLikeButton
							cdPrestador={professional.cdPrestadorFisico}
							cdEspecialidade={professional.cdEspecialidade}
							cdSubEspecialidade={professional.cdSubEspecialidade}
							selected
							asButton
						/>
					) : (
						<Button
							onClick={() => onScheduleAppointment({professional})}
							disabled={!professional.flAgendaDisponivel}
							size="small">
							{primaryActionLabel}
						</Button>
					)}
				</Stack>
			</StyledCard>
		</StyledGrid.Item>
	);
};

export {ProviderCard};
